import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const { 
  FiEye, FiUsers, FiShield, FiHeart, FiUser, FiBook, FiMessageSquare,
  FiBriefcase, FiHome, FiGrid, FiSearch, FiFilter, FiTrendingUp,
  FiActivity, FiClock, FiCheckCircle, FiAlertCircle, FiTarget,
  FiBarChart2, FiPieChart, FiDollarSign, FiAward, FiZap, FiPackage
} = FiIcons;

const SuperAdminView = () => {
  const { user } = useAuth();
  const [selectedUserGroup, setSelectedUserGroup] = useState('all');
  const [selectedPortal, setSelectedPortal] = useState('internal');
  const [searchTerm, setSearchTerm] = useState('');
  const [timeRange, setTimeRange] = useState('7d');

  const userGroups = [
    { 
      id: 'all', 
      name: 'All Users', 
      icon: FiUsers, 
      count: 1250, 
      active: 892,
      color: 'blue',
      growth: '+12.5%'
    },
    { 
      id: 'superadmin', 
      name: 'Super Admins', 
      icon: FiShield, 
      count: 3, 
      active: 3,
      color: 'purple',
      growth: '0%'
    },
    { 
      id: 'admin', 
      name: 'Admins', 
      icon: FiShield, 
      count: 12, 
      active: 10,
      color: 'indigo',
      growth: '+2'
    },
    { 
      id: 'provider', 
      name: 'Providers', 
      icon: FiHeart, 
      count: 245, 
      active: 198,
      color: 'red',
      growth: '+8.3%'
    },
    { 
      id: 'patient', 
      name: 'Patients', 
      icon: FiUser, 
      count: 990, 
      active: 681,
      color: 'green',
      growth: '+15.2%'
    }
  ];

  const portals = [
    { 
      id: 'internal', 
      name: 'Internal Portal', 
      icon: FiShield,
      users: 15,
      sessions: 234,
      avgDuration: '12m 34s',
      color: 'blue'
    },
    { 
      id: 'provider', 
      name: 'Provider Portal', 
      icon: FiHeart,
      users: 245,
      sessions: 1432,
      avgDuration: '8m 15s',
      color: 'red'
    },
    { 
      id: 'patient', 
      name: 'Patient Portal', 
      icon: FiUser,
      users: 990,
      sessions: 3421,
      avgDuration: '6m 42s',
      color: 'green'
    },
    { 
      id: 'lms', 
      name: 'Learning Hub', 
      icon: FiBook,
      users: 342,
      sessions: 876,
      avgDuration: '24m 18s',
      color: 'orange'
    },
    { 
      id: 'community', 
      name: 'Community', 
      icon: FiUsers,
      users: 567,
      sessions: 1923,
      avgDuration: '15m 32s',
      color: 'pink'
    },
    { 
      id: 'dealrooms', 
      name: 'Dealrooms', 
      icon: FiBriefcase,
      users: 123,
      sessions: 456,
      avgDuration: '18m 45s',
      color: 'indigo'
    }
  ];

  const activityData = [
    {
      id: 1,
      user: 'Dr. Sarah Mitchell',
      role: 'Provider',
      portal: 'Provider Portal',
      action: 'Created new dealroom',
      timestamp: '2 minutes ago',
      status: 'success',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop'
    },
    {
      id: 2,
      user: 'John Patient',
      role: 'Patient',
      portal: 'Patient Portal',
      action: 'Completed appointment booking',
      timestamp: '5 minutes ago',
      status: 'success',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop'
    },
    {
      id: 3,
      user: 'Dr. Michael Chen',
      role: 'Provider',
      portal: 'Learning Hub',
      action: 'Enrolled in Advanced Implantology course',
      timestamp: '12 minutes ago',
      status: 'info',
      avatar: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=100&h=100&fit=crop'
    },
    {
      id: 4,
      user: 'Admin User',
      role: 'Admin',
      portal: 'Internal Portal',
      action: 'Updated system settings',
      timestamp: '1 hour ago',
      status: 'warning',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop'
    },
    {
      id: 5,
      user: 'Emma Wilson',
      role: 'Patient',
      portal: 'Community',
      action: 'Posted in discussion forum',
      timestamp: '2 hours ago',
      status: 'success',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop'
    }
  ];

  const metrics = [
    {
      label: 'Total Active Users',
      value: '892',
      change: '+12.5%',
      icon: FiUsers,
      color: 'blue',
      trend: 'up'
    },
    {
      label: 'Portal Sessions',
      value: '8,342',
      change: '+24.3%',
      icon: FiActivity,
      color: 'green',
      trend: 'up'
    },
    {
      label: 'Avg Session Duration',
      value: '11m 23s',
      change: '+3.2%',
      icon: FiClock,
      color: 'purple',
      trend: 'up'
    },
    {
      label: 'System Health',
      value: '99.8%',
      change: '+0.1%',
      icon: FiCheckCircle,
      color: 'green',
      trend: 'up'
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      blue: 'bg-blue-50 text-blue-600 border-blue-200',
      purple: 'bg-purple-50 text-purple-600 border-purple-200',
      indigo: 'bg-indigo-50 text-indigo-600 border-indigo-200',
      red: 'bg-red-50 text-red-600 border-red-200',
      green: 'bg-green-50 text-green-600 border-green-200',
      orange: 'bg-orange-50 text-orange-600 border-orange-200',
      pink: 'bg-pink-50 text-pink-600 border-pink-200'
    };
    return colors[color] || colors.blue;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'bg-green-100 text-green-800';
      case 'warning': return 'bg-yellow-100 text-yellow-800';
      case 'error': return 'bg-red-100 text-red-800';
      case 'info': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (user?.role !== 'SuperAdmin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-red-50 to-orange-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl p-8 shadow-lg border border-red-200 max-w-md">
          <div className="flex items-center space-x-3 mb-4">
            <SafeIcon icon={FiAlertCircle} className="w-8 h-8 text-red-600" />
            <h2 className="text-2xl font-bold text-gray-900">Access Denied</h2>
          </div>
          <p className="text-gray-600">You don't have permission to access the SuperAdmin View. This feature is restricted to SuperAdmin users only.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-indigo-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <div className="p-3 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-xl shadow-lg">
                  <SafeIcon icon={FiEye} className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">SuperAdmin View</h1>
                  <p className="text-gray-600">Monitor all user groups and portal activities</p>
                </div>
              </div>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center space-x-3">
              <select 
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="90d">Last 90 Days</option>
              </select>
              <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2">
                <SafeIcon icon={FiBarChart2} className="w-4 h-4" />
                <span>Analytics</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {metrics.map((metric, index) => (
            <motion.div
              key={metric.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg ${getColorClasses(metric.color)}`}>
                  <SafeIcon icon={metric.icon} className="w-6 h-6" />
                </div>
                <span className={`text-sm font-medium ${metric.trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
                  {metric.change}
                </span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
              <p className="text-sm text-gray-600 mt-1">{metric.label}</p>
            </motion.div>
          ))}
        </div>

        {/* User Groups */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-4">User Groups</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            {userGroups.map((group) => (
              <button
                key={group.id}
                onClick={() => setSelectedUserGroup(group.id)}
                className={`p-4 rounded-xl border-2 transition-all ${
                  selectedUserGroup === group.id
                    ? `${getColorClasses(group.color)} border-2 shadow-md`
                    : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className={`p-2 rounded-lg ${getColorClasses(group.color)}`}>
                    <SafeIcon icon={group.icon} className="w-5 h-5" />
                  </div>
                  <span className="text-xs font-medium text-green-600">{group.growth}</span>
                </div>
                <p className="text-2xl font-bold text-gray-900">{group.count}</p>
                <p className="text-sm text-gray-600 mb-1">{group.name}</p>
                <div className="flex items-center space-x-1 text-xs text-gray-500">
                  <SafeIcon icon={FiActivity} className="w-3 h-3" />
                  <span>{group.active} active</span>
                </div>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Portal Overview */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Portal Activity</h2>
            <div className="flex items-center space-x-2">
              <button className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                View All
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portals.map((portal, index) => (
              <motion.div
                key={portal.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="p-4 border-2 border-gray-200 rounded-xl hover:shadow-md transition-all"
              >
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`p-3 rounded-lg ${getColorClasses(portal.color)}`}>
                    <SafeIcon icon={portal.icon} className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{portal.name}</h3>
                    <p className="text-xs text-gray-500">{portal.users} users</p>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Sessions</span>
                    <span className="font-semibold text-gray-900">{portal.sessions}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Avg Duration</span>
                    <span className="font-semibold text-gray-900">{portal.avgDuration}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Real-Time Activity</h2>
            <div className="flex items-center space-x-2">
              <div className="flex items-center space-x-1 text-sm text-gray-500">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span>Live</span>
              </div>
            </div>
          </div>
          <div className="space-y-4">
            {activityData.map((activity, index) => (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.9 + index * 0.1 }}
                className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <img 
                  src={activity.avatar} 
                  alt={activity.user}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <p className="font-semibold text-gray-900">{activity.user}</p>
                    <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full">
                      {activity.role}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{activity.action}</p>
                  <div className="flex items-center space-x-3 mt-1">
                    <span className="text-xs text-gray-500">{activity.portal}</span>
                    <span className="text-xs text-gray-400">•</span>
                    <span className="text-xs text-gray-500">{activity.timestamp}</span>
                  </div>
                </div>
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(activity.status)}`}>
                  {activity.status}
                </span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SuperAdminView;